import { ReactNode, lazy, Suspense, useState, useEffect } from "react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { MobileBottomNav } from "./MobileBottomNav";
import { UnderConstructionBanner } from "@/components/UnderConstructionBanner";

import { useSiteSettings } from "@/hooks/useSiteSettings";
import { isBR } from "@/lib/market";

// Lazy load non-critical widgets to improve FCP
const NewsletterPopup = lazy(() => import("@/components/NewsletterPopup").then(m => ({ default: m.NewsletterPopup })));
const SocialFloatingWidget = lazy(() => import("@/components/SocialFloatingWidget").then(m => ({ default: m.SocialFloatingWidget })));
const ConsumptionFloatingWidget = lazy(() => import("@/components/ConsumptionFloatingWidget").then(m => ({ default: m.ConsumptionFloatingWidget })));

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { underConstructionEnabled, underConstructionMessage } = useSiteSettings();
  const [showDeferredWidgets, setShowDeferredWidgets] = useState(false);

  // Defer loading of non-critical widgets until after initial paint
  useEffect(() => {
    // Use requestIdleCallback for better performance, with setTimeout fallback
    const loadDeferred = () => setShowDeferredWidgets(true);
    
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(loadDeferred, { timeout: 2000 });
      return () => window.cancelIdleCallback(id);
    } else {
      const id = setTimeout(loadDeferred, 1000);
      return () => clearTimeout(id);
    }
  }, []);

  // SEO: never show "Site em construção" on production hostnames — it kills E-E-A-T.
  // Banner is allowed only in dev / Lovable preview / sandbox environments.
  const isProductionHost =
    typeof window !== "undefined" &&
    /(^|\.)hexatech\.pt$|(^|\.)hexatech\.lovable\.app$|(^|\.)txfactory\.pt$|(^|\.)hexatechbrasil\.com\.br$/.test(window.location.hostname);

  return (
    <div className="flex min-h-dvh flex-col pt-16" data-public-layout="true">
      <Header />
      {isBR && (
        <div className="bg-primary text-primary-foreground text-center text-xs py-1.5 px-3">
          HEXATECH Brasil · Preços em R$ · Vendas exclusivas para empresas (CNPJ)
        </div>
      )}
      {underConstructionEnabled && !isProductionHost && (
        <UnderConstructionBanner message={underConstructionMessage} />
      )}

      <main id="main-content" className="flex-1">{children}</main>
      <div className="pb-[calc(4rem+env(safe-area-inset-bottom))] lg:pb-0">
        <Footer />
      </div>
      <MobileBottomNav />
      {showDeferredWidgets && (
        <Suspense fallback={null}>
          <NewsletterPopup />
          <SocialFloatingWidget />
          <ConsumptionFloatingWidget />
        </Suspense>
      )}
    </div>
  );
}
